/* eslint-disable no-param-reassign */
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
} from '@mui/icons-material';
import { ButtonGroup, LinearProgress } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Grid } from '@mui/system';
import { isEmpty, partition } from 'lodash';
import { useLocation } from 'react-router-dom';
import uuid from 'react-uuid';
import { ValuationApproachPtCompGroup } from 'api';
import { FINANCIALS_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { GRID_NUMBER_CHECKBOX } from 'common/constants/gridType';
import { VALUATIONS_PAGE_KEY, VALUATIONS_PAGE_VALUE } from 'common/constants/notes';
import {
  PDF_EXPORT_QUERY_PARAM_EXPORT_DESCRIPTION_PAGE,
} from 'common/constants/pdfExport';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, CustomContainer } from 'components';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { ValueEditor } from 'components/Spreadsheet/components';
import CompGroupContext from 'context/CompGroupContext';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { PERFORMANCE_METRICS_SPREADSHEET_SLUG } from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/common/constants/performanceMetrics';
import { CompGroupsOptions } from 'pages/Valuations/components/CompGroups/components/CompGroupsOptions';
import { PdfExportTargets, PRIVATE_TRANSACTIONS, SORT_ASC, SORT_DESC } from 'pages/Valuations/util/constants';
import { packDescriptionsForPdf } from 'pages/Valuations/util/packDescriptionsForPdf';
import { sortGptTransactionsForPdfDescriptions } from 'pages/Valuations/util/sortComparisonsForPdfExport';
import {
  ACQUIRER_NAME_ID,
  GPT_TRANSACTION,
  VALUATION_TC_TABLE_SLUG,
} from 'pages/ValuationsAllocation/approaches/TransactionComps/config/constants';
import getRowConfig from 'pages/ValuationsAllocation/approaches/TransactionComps/config/getRowConfig';
import rowTransformer from 'pages/ValuationsAllocation/approaches/TransactionComps/config/rowTransformer';
import { useCellOptions, useValuationNotesCache } from 'pages/ValuationsAllocation/hooks';
import { TransactionCompsAttributes } from 'pages/ValuationsAllocation/types';
import { extractSpecificApproachFromApproach, getApproachTableName } from 'pages/ValuationsAllocation/util';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useNotes } from 'services/hooks/notes';
import theme from 'theme';
import { getArrayValue, getStringValue, sortByDate, sortByNumber, sortByString } from 'utilities';
import { useCapIqTransactions } from './hooks';
import {
  createColumns as perfMetricsCreateColumn,
  rowConfig as perfMetricsRowConfig,
} from './PerformanceMetrics/config';
import {
  ReverseParsedTCCompGroup,
  ReverseParsedTransaction,
  ReverseParsedValuationApproachGPT,
  TransactionCompsCell,
  TransactionCompsProps,
  TransactionCompsTableData,
  TransactionQueryResponse,
  WritableCompGroupTransaction,
} from './types';
import WidgetDrawer from '../../../../components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from '../../../../components/Drawer/widget-drawer/WidgetDrawer.props';

const VALUATION_GPT_TRANSACTIONS_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${VALUATION_TC_TABLE_SLUG}-container`;
const VALUATION_GPT_PERFORMANCE_METRICS_CONTAINER_CLASS = `${VALUATIONS_SLUG}-gpt-${PERFORMANCE_METRICS_SPREADSHEET_SLUG}-container`;
const EMPTY_PDF_VALUE = '-';

const TransactionComps: FC<TransactionCompsProps> = props => {
  const { spreadsheets, approach, onChange, workbook, financials } = props;
  const { compsSheet, performanceMetricsSheet } = spreadsheets;
  const { tableData: tD } = compsSheet;
  const tableData = tD as TransactionCompsTableData;
  const { valuations_approach_gpt } = approach;
  const approachPanelId = approach.panelId;
  const compGroupsRef = useRef(null);
  const [openCompGroupsOptions, setOpenCompGroupsOptions] = useState(false);
  const [lastSortedColumn, setLastSortedColumn] = useState<{ [key: string]: string } | null>(null);
  const [collapsibleColumns, setCollapsibleColumns] = useState({
    [ACQUIRER_NAME_ID]: false,
  });
  const { compGroups, notesInApproach, setAreThereChanges, setNotesInApproach, resetConfiguration, registerApproachFormat } =
    useContext(ValuationContext);
  const [format, formatDispatch] = useFormat({ page: FINANCIALS_CURRENCY_PAGE });
  const { notes, setNotes, notesHasChanged, setNotesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const { notes: cachedNotes } = useValuationNotesCache({
    cachedNotes: notesInApproach,
    notes,
    notesHasChanged,
    panelId: approachPanelId,
    setCachedNotes: setNotesInApproach,
    setNotes,
    setNotesHasChanged,
  });
  const { isDisabled, gpcOptions } = tableData;
  const { isExportMode } = useLayoutContext();
  const location = useLocation();

  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;

  const [cellOptions, onChangeHandler, isLoading] = useCellOptions<ReverseParsedValuationApproachGPT>(
    onChange,
    approach.valuations_approach_gpt
  );
  const handleOnChange = onChangeHandler as (
    cell: TransactionCompsCell,
    expression: string
  ) => Promise<void | TransactionQueryResponse>;

  const { setNumericValues } = useCapIqTransactions(compsSheet.fieldAttributes as TransactionCompsAttributes);

  useEffect(() => {
    const tabId = getApproachTableName({ approach: approach as any });
    registerApproachFormat?.(tabId, format, formatDispatch);
  }, [approach, format, formatDispatch, registerApproachFormat]);

  const updateApproach = useCallback(
    updatedTableData => {
      const approachData = updatedTableData.approach;
      const companyName = getStringValue(companyInfo?.name);
      const specificApproach = extractSpecificApproachFromApproach(approachData);
      const transactionCompsApproach = specificApproach as ReverseParsedValuationApproachGPT;
      compsSheet.reset({
        rowConfig: getRowConfig({
          allCompGroups: compGroups,
          approach: transactionCompsApproach,
          companyName: getStringValue(companyInfo?.name),
          gpcOptions,
          isDisabled,
        }),
        // Setting these two parameters to null (`financialsPeriods`, `financialStatementId`) because we cannot define the function without them, but we don't need them in this case.
        columns: rowTransformer({
          approach: specificApproach,
          financials,
          sortedColumn: approachData?.updatedSortedColumn || {},
        }),
      });
      performanceMetricsSheet.reset({
        rowConfig: perfMetricsRowConfig({ companyName, valuationsApproachGpt: transactionCompsApproach }),
        columns: perfMetricsCreateColumn({ valuationsApproachGpt: transactionCompsApproach, financials }),
      });
      resetConfiguration?.();
    },
    [
      compGroups,
      companyInfo,
      compsSheet,
      financials,
      gpcOptions,
      isDisabled,
      resetConfiguration,
      performanceMetricsSheet,
    ]
  );

  const handleToggle = () => {
    setOpenCompGroupsOptions(prevOpen => !prevOpen);
  };

  const handleTransactionCellChange = useCallback(
    async (cell: TransactionCompsCell, expression) => {
      const changeResponse = await handleOnChange(cell, expression as string);
      if (changeResponse) {
        const { gpt_transactions } = tableData.approach.valuations_approach_gpt;
        const transactionToUpdate = gpt_transactions.find(({ row_ref }) => row_ref === cell.alias);
        if (!transactionToUpdate) {
          return;
        }
        transactionToUpdate.target_name = changeResponse.transaction_target_name;
        transactionToUpdate.name = changeResponse.transaction_target_name;
        transactionToUpdate.transaction_deal_resolution = changeResponse.transaction_deal_resolution;
        transactionToUpdate.transaction_comments = changeResponse.transaction_comments;
        transactionToUpdate.business_description = changeResponse.business_description;
        transactionToUpdate.transaction_date =
          changeResponse.transaction_closed_date ?? changeResponse.transaction_agreement_date;
        transactionToUpdate.acquirer_name = changeResponse.transaction_buyer_name;
        transactionToUpdate.cap_iq_transaction_id = expression;
        setNumericValues(transactionToUpdate, changeResponse);
        transactionToUpdate.comes_from_capital_iq = true;
        // this will regenerate the row config and apply the parser again
        updateApproach({ ...tableData, approach: { ...tableData.approach } });
        // Persist to parent so reverseParse uses enriched data
        onChange(cell, expression as string);
      } else if (cell.isGptRow && cell.gridType !== GRID_NUMBER_CHECKBOX) {
        updateApproach({ ...tableData, approach: { ...tableData.approach } });
      }
    },
    [handleOnChange, tableData, updateApproach, setNumericValues, onChange]
  );

  const isSame = (currentItem: ReverseParsedTransaction, transaction: ReverseParsedTransaction) => {
    const isSameTargetName = currentItem.target_name?.toLowerCase() === transaction.target_name?.toLowerCase();
    const isSameAcquirerName = currentItem.acquirer_name?.toLowerCase() === transaction.acquirer_name?.toLowerCase();
    return isSameTargetName && isSameAcquirerName;
  };

  const getTransactionsPostCompGroupChange = useCallback(
    (
      transactions: WritableCompGroupTransaction[],
      transactionsToDelete: number[],
      compGroupsData: ReverseParsedTCCompGroup[] | undefined,
      useLatestVersion: boolean
    ) => {
      const { gpt_transactions } = extractSpecificApproachFromApproach(
        tableData.approach
      ) as ReverseParsedValuationApproachGPT;
      const remainingTransactions = gpt_transactions.filter(
        currentTransaction => !transactionsToDelete.includes(currentTransaction.id ?? currentTransaction.temp_id)
      );
      const transactionsWithUpdatedCompGroup = remainingTransactions.map(transaction => {
        const transactionCompGroupId = transaction.pt_approach_comp_group ?? transaction.compgroupId;
        if (compGroupsData) {
          const hasCompGroup = compGroupsData.some(compGroup => compGroup.id === transactionCompGroupId);
          if (!hasCompGroup) {
            const { pt_approach_comp_group, comp_group_id, comp_group_name, temp_ref, ...newTransactionObj } =
              transaction;
            return newTransactionObj;
          }
        }

        return transaction;
      });

      if (transactions) {
        transactions.forEach(transaction => {
          const currentTransactionIdx = transactionsWithUpdatedCompGroup.findIndex(current =>
            isSame(current, transaction)
          );
          if (currentTransactionIdx !== -1) {
            const currentTransactionData = transactionsWithUpdatedCompGroup[currentTransactionIdx];
            if (useLatestVersion) {
              transactionsWithUpdatedCompGroup[currentTransactionIdx] = {
                id: currentTransactionData.id,
                ...transaction,
              };
            } else {
              const { comp_group_id, comp_group_name, temp_ref, pt_approach_comp_group, ...updatedCurrentTransaction } =
                currentTransactionData;
              transactionsWithUpdatedCompGroup[currentTransactionIdx] = {
                id: currentTransactionData.id,
                ...updatedCurrentTransaction,
              };
            }
          } else {
            transactionsWithUpdatedCompGroup.push(transaction);
          }
        });
      }

      return transactionsWithUpdatedCompGroup;
    },
    [tableData]
  );

  const getCompGroupsData = useCallback(
    (transactions: WritableCompGroupTransaction[], compGroupToDelete: number) => {
      const { valuationapproachptcompgroup_set } = extractSpecificApproachFromApproach(
        tableData.approach
      ) as ReverseParsedValuationApproachGPT;
      const compGroupsData = getArrayValue(valuationapproachptcompgroup_set).filter(
        item => compGroupToDelete !== item.comp_group
      );
      if (transactions) {
        const newCompGroups = transactions
          .filter(transaction => transaction.comp_group_id && compGroupToDelete !== transaction.comp_group_id)
          .filter(item => !compGroupsData.some(compGroupData => compGroupData.id === item.comp_group_id))
          .map(transactionCompGroupItem => ({
            isNew: true,
            comp_group: transactionCompGroupItem.comp_group_id,
            comp_group_name: transactionCompGroupItem.comp_group_name,
            use_latest_comp_group_version: true,
          })) as ReverseParsedTCCompGroup[];

        const uniqueNewCompGroupsData = [...new Map(newCompGroups.map(item => [item.comp_group, item])).values()];
        return [...compGroupsData, ...uniqueNewCompGroupsData];
      }
    },
    [tableData]
  );

  const getDeletedCompGroupsIDs = useCallback(
    (compGroupToDelete: number) => {
      const { valuationapproachptcompgroup_set } = tableData.approach.valuations_approach_gpt;
      const findMatch = (compGroupItem: ValuationApproachPtCompGroup) => compGroupToDelete === compGroupItem.comp_group;
      return getArrayValue(valuationapproachptcompgroup_set)
        .filter(compGroupItem => !compGroupItem.isNew && findMatch(compGroupItem))
        .map(compGroupItem => compGroupItem.id);
    },
    [tableData]
  );

  const saveCompGroups = useCallback(
    (
      transactionsFromCG: WritableCompGroupTransaction[],
      useLatestVersion: boolean,
      transactionsToDelete: number[],
      compGroupToDelete: number
    ) => {
      // useLatestVersion will establish link between the transaction and the comp group for future offscreen updates
      // transactionsToDelete will be used when we remove a transaction from an already attached comp group using the modal
      // same as above for compGroupToDelete
      const compGroupsData = getCompGroupsData(transactionsFromCG, compGroupToDelete);
      const updatedTransactions = getTransactionsPostCompGroupChange(
        transactionsFromCG,
        transactionsToDelete,
        compGroupsData,
        useLatestVersion
      );
      const specificApproach = extractSpecificApproachFromApproach(tableData.approach);
      const valuationApproachGPT: ReverseParsedValuationApproachGPT = {
        ...specificApproach,
        gpt_transactions: updatedTransactions,
        valuationapproachptcompgroup_set: compGroupsData,
      } as ReverseParsedValuationApproachGPT;

      if (!isEmpty(transactionsToDelete)) {
        valuationApproachGPT.deleted_gpt_transactions = transactionsToDelete;
      }
      if (compGroupToDelete) {
        const deletedCompGroups = getDeletedCompGroupsIDs(compGroupToDelete);
        valuationApproachGPT.deleted_comp_groups = deletedCompGroups as number[];
      }
      tableData.approach.valuations_approach_gpt = {
        ...valuationApproachGPT,
        gpt_transactions: valuationApproachGPT.gpt_transactions.map((transaction, index) => ({
          ...transaction,
          order: index,
        })),
      };
      updateApproach({
        ...tableData,
        approach: {
          ...tableData.approach,
          valuations_approach_gpt: valuationApproachGPT,
        },
      });
    },
    [getCompGroupsData, getDeletedCompGroupsIDs, getTransactionsPostCompGroupChange, tableData, updateApproach]
  );

  const deleteCompGroup = useCallback(
    compGroupToDelete => {
      const { gpt_transactions, valuationapproachptcompgroup_set, deleted_gpt_transactions, deleted_comp_groups } =
        tableData.approach.valuations_approach_gpt;
      const currentDeletedTransactions = deleted_gpt_transactions || [];
      const currentDeletedCompGroups = deleted_comp_groups || [];

      const transactionResult = partition(gpt_transactions, item => {
        const compGroupId = item.pt_approach_comp_group ?? item.comp_group_id;
        return compGroupId === compGroupToDelete;
      });

      const compGroupData = getArrayValue(valuationapproachptcompgroup_set).filter(compGroup => {
        const compGroupId = compGroup.id ?? compGroup.comp_group;
        return compGroupId !== compGroupToDelete;
      });

      const transactionsToDelete = transactionResult[0];
      const remainingTransactions = transactionResult[1];

      const updatedApproach = {
        gpt_transactions: remainingTransactions,
        valuationapproachptcompgroup_set: compGroupData,
        deleted_comp_groups: [...new Set([...currentDeletedCompGroups, ...[compGroupToDelete]])],
        deleted_gpt_transactions: [] as number[],
      };

      if (transactionsToDelete) {
        updatedApproach.deleted_gpt_transactions = [
          ...currentDeletedTransactions,
          ...getArrayValue(transactionsToDelete)
            .filter(({ id }) => id)
            .map(({ id }) => id as number),
        ];
      }
      tableData.approach.valuations_approach_gpt = {
        ...tableData.approach.valuations_approach_gpt,
        ...updatedApproach,
        gpt_transactions: updatedApproach.gpt_transactions.map((transaction, index) => ({
          ...transaction,
          order: index,
        })),
      };
      updateApproach(tableData);
    },
    [tableData, updateApproach]
  );

  const generateNewCompanies = (count: number) => {
    const companiesArray = [];

    for (let i = 0; i < count; i++) {
      const newCompany = {
        row_ref: uuid(),
        name: GPT_TRANSACTION,
        target_name: '',
        transaction_deal_resolution: null,
        transaction_comments: null,
        business_description: null,
        acquirer_name: null,
        transaction_date: null,
        enterprise_value: null,
        ltm_revenue: null,
        ltm_ebitda: null,
        ltm_revenue_enabled: true,
        ltm_ebitda_enabled: true,
        isNew: true,
        comes_from_capital_iq: false,
        ntm_revenue: null,
        ntm_ebitda: null,
      };
      companiesArray.push(newCompany);
    }

    return companiesArray;
  };

  const addTransaction = (newRows = 1) => {
    const { tableData: tD } = compsSheet;
    const { approach: compSheetTableData } = tD as TransactionCompsTableData;

    const newCompanies = generateNewCompanies(newRows) as unknown as ReverseParsedTransaction[];

    compSheetTableData.valuations_approach_gpt = {
      ...tableData.approach.valuations_approach_gpt,
      gpt_transactions: [...newCompanies, ...compSheetTableData.valuations_approach_gpt.gpt_transactions].map(
        (transaction, index) => ({
          ...transaction,
          order: index,
        })
      ),
    };
    updateApproach({ ...compsSheet.tableData, approach: compSheetTableData });
  };

  const removeTransaction = (deletedTransaction: ReverseParsedTransaction) => {
    const { valuationapproachptcompgroup_set, deleted_comp_groups } = valuations_approach_gpt;
    const compGroupFromDeleted = deletedTransaction.comp_group_id || deletedTransaction.pt_approach_comp_group;
    const currentDeletedCompGroups = deleted_comp_groups || [];

    const { tableData } = compsSheet;
    const { approach: compSheetTableData } = tableData as TransactionCompsTableData;
    // 1. update the specific approach to no longer include this thing
    const remainingTransactions = compSheetTableData.valuations_approach_gpt.gpt_transactions.filter(transaction => {
      const transactionId = transaction.row_ref;
      const deletedTransactionId = deletedTransaction.row_ref;
      return transactionId !== deletedTransactionId;
    });

    // Remove de comp group relation of the other companies
    // with the same comp group as the deleted one
    remainingTransactions.forEach(transaction => {
      if (compGroupFromDeleted && transaction.pt_approach_comp_group === compGroupFromDeleted) {
        delete transaction.pt_approach_comp_group;
      }
      if (compGroupFromDeleted && transaction.comp_group_id === compGroupFromDeleted) {
        delete transaction.comp_group_id;
      }
    });

    const objectKeyCompGroup = valuationapproachptcompgroup_set?.find(
      item => item.id === deletedTransaction.pt_approach_comp_group
    );

    const valuationKeysToSave = valuationapproachptcompgroup_set?.filter(
      item => item.comp_group !== objectKeyCompGroup?.comp_group
    );

    // 2. get the id of the transaction to delete
    const deletedId = deletedTransaction.id;

    // 3. find the existing list of deleted transactions
    let { deleted_gpt_transactions } = compSheetTableData.valuations_approach_gpt;

    // 4. if there is a deleted id, include it in the list if one exists
    if (deletedId) {
      deleted_gpt_transactions = deleted_gpt_transactions
        ? [...getArrayValue(compSheetTableData.valuations_approach_gpt.deleted_gpt_transactions), deletedId]
        : [deletedId];
    }

    // 5. update the approach
    compSheetTableData.valuations_approach_gpt = {
      ...compSheetTableData.valuations_approach_gpt,
      gpt_transactions: remainingTransactions,
      deleted_gpt_transactions,
      valuationapproachptcompgroup_set: valuationKeysToSave,
    };

    // 6. if the pt has a comp group, remove it from the list of deleted comp groups
    if (deletedTransaction.pt_approach_comp_group) {
      compSheetTableData.valuations_approach_gpt = {
        ...compSheetTableData.valuations_approach_gpt,
        deleted_comp_groups: [...currentDeletedCompGroups, compGroupFromDeleted],
      };
    }
    updateApproach({ ...compsSheet.tableData, approach: compSheetTableData });
    setAreThereChanges?.(true);
  };

  const compGroupContextValue = useMemo(
    () => ({
      saveCompGroups,
      deleteCompGroup,
      tableData,
      approachType: PRIVATE_TRANSACTIONS,
      cellOptions,
    }),
    [saveCompGroups, deleteCompGroup, tableData, cellOptions]
  );

  const sortTransactions = useCallback(
    (key, isStringColumn = false, isDateColumn = false) => {
      const currentSortOrderColumn = getStringValue(lastSortedColumn?.[key]);
      const currentSortedColumnIsDesc = currentSortOrderColumn === SORT_DESC;

      const updatedSortedColumn = {
        [key]: currentSortedColumnIsDesc ? SORT_ASC : SORT_DESC,
      };

      tableData.approach.valuations_approach_gpt.gpt_transactions.sort((a, b) => {
        if (a?.[key] && b?.[key]) {
          setLastSortedColumn(updatedSortedColumn);

          const sortOrder = currentSortedColumnIsDesc ? SORT_ASC : SORT_DESC;
          if (isDateColumn) {
            return sortByDate(a, b, key, sortOrder);
          }
          if (isStringColumn) {
            return sortByString(a, b, key, sortOrder);
          }
          return sortByNumber(a, b, key, sortOrder);
        }
        return 0;
      });
      const sortedTransactions = tableData.approach.valuations_approach_gpt.gpt_transactions;

      const valuationApproachGPT = {
        ...tableData.approach.valuations_approach_gpt,
        gpt_transactions: sortedTransactions.map((comp, index) => ({
          ...comp,
          order: index,
        })),
      };

      tableData.approach.valuations_approach_gpt = {
        ...valuationApproachGPT,
        gpt_transactions: sortedTransactions.map((transaction, index) => ({
          ...transaction,
          order: index,
        })),
      };
      updateApproach({
        ...tableData,
        approach: {
          ...tableData.approach,
          valuations_approach_gpt: valuationApproachGPT,
          updatedSortedColumn,
        },
      });
    },
    [tableData, lastSortedColumn, updateApproach]
  );

  const gptComparisons = valuations_approach_gpt?.gpt_transactions;
  const gptDescriptionChunks = useMemo(() => {
    if (!isExportMode) {
      return [];
    }
    const transactions = sortGptTransactionsForPdfDescriptions(gptComparisons ?? []);
    return packDescriptionsForPdf(transactions, t => t.business_description);
  }, [gptComparisons, isExportMode]);
  const currentDescriptionPageIndex = useMemo(() => {
    if (!isExportMode) {
      return 0;
    }
    const pageFromQuery = new URLSearchParams(location.search).get(PDF_EXPORT_QUERY_PARAM_EXPORT_DESCRIPTION_PAGE);
    const parsedIndex = Number(pageFromQuery ?? '0');
    return Number.isNaN(parsedIndex) ? 0 : parsedIndex;
  }, [location.search, isExportMode]);
  // Clamp the requested chunk to the available range so an out-of-range index
  // (e.g. usePdfPages built N pages from a stale `gpt_transactions.length`)
  // falls back to the last real chunk instead of rendering empty and being
  // reported as `missing` by the orchestrator.
  const safeDescriptionPageIndex =
    gptDescriptionChunks.length === 0
      ? 0
      : Math.min(Math.max(0, currentDescriptionPageIndex), gptDescriptionChunks.length - 1);
  const currentGptDescriptionChunk = gptDescriptionChunks[safeDescriptionPageIndex] ?? [];
  let transactionCompsExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoading || !compsSheet?.data) {
    transactionCompsExportStatus = 'loading';
  } else if (isEmpty(gptComparisons)) {
    transactionCompsExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.TRANSACTION_COMPS.id,
    status: transactionCompsExportStatus,
    enabled: isExportMode,
  });
  let transactionCompsDescriptionsExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoading || !compsSheet?.data) {
    transactionCompsDescriptionsExportStatus = 'loading';
  } else if (isEmpty(currentGptDescriptionChunk)) {
    transactionCompsDescriptionsExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.TRANSACTION_COMPS_DESCRIPTIONS.id,
    status: transactionCompsDescriptionsExportStatus,
    enabled: isExportMode,
  });

  return (
    <>
      {/* `data-ref={TRANSACTION_COMPS.id}` wraps both the transactions table and
          the performance metrics so the PDF capture for this page includes
          both. The descriptions block is rendered as a separate sibling so it
          is captured under its own `[data-ref=TRANSACTION_COMPS_DESCRIPTIONS]`
          page (one chunk per export page) and not duplicated here. */}
      <CustomContainer
        data-ref={PdfExportTargets.TRANSACTION_COMPS.id}
        style={isExportMode ? EXPORT_MODE_STYLES : {}}>
        {/* Transactions */}
        <CustomCompactContainer className={VALUATION_GPT_TRANSACTIONS_CONTAINER_CLASS}>
          <Grid size={{ xs: 12 }}>
            <CompGroupContext.Provider value={compGroupContextValue}>
              {isLoading && <LinearProgress />}
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore: ScalarSpreadsheet is not typed */}
              <ScalarSpreadsheet
                {...compsSheet}
                key={compsSheet.name}
                onChange={handleTransactionCellChange}
                sheet={compsSheet}
                workbook={workbook}
                format={format}
                formatDispatch={formatDispatch}
                deleteRowFn={removeTransaction}
                tableTerms={{
                  tableSlug: VALUATION_TC_TABLE_SLUG,
                }}
                addMultipleRows={addTransaction}
                // @ts-expect-error: ScalarSpreadsheet is not typed
                editorForTitles={ValueEditor}
                allowSortColumn
                // @ts-expect-error: ScalarSpreadsheet is not typed
                sortColumnFn={sortTransactions}
                // @ts-expect-error: ScalarSpreadsheet is not typed
                sortedColumn={lastSortedColumn}
                collapsibleColumns={collapsibleColumns}
                setCollapsibleColumns={setCollapsibleColumns}
              />
            </CompGroupContext.Provider>
          </Grid>

          {/* Add comparable transaction */}
          {!isExportMode && (
            <Grid display="inline-flex" position="relative">
              <ButtonGroup
                color="primary"
                variant="outlined"
                aria-label="split button"
                style={{
                  borderRadius: theme.spacing(0.5),
                  paddingLeft: theme.spacing(1.5),
                }}>
                <Button
                  id="add-comparable-transaction-btn"
                  onClick={() => addTransaction()}
                  disabled={isDisabled}
                  startIcon={<AddIcon />}
                  style={{
                    borderBottomLeftRadius: theme.spacing(0.5),
                    borderTopLeftRadius: theme.spacing(0.5),
                  }}>
                  Add Comparable Transaction
                </Button>
                <Button
                  id="comparable-group-btn"
                  ref={compGroupsRef}
                  size="small"
                  aria-controls={openCompGroupsOptions ? 'split-button-menu' : undefined}
                  aria-expanded={openCompGroupsOptions ? 'true' : undefined}
                  aria-label="select comp groups options"
                  aria-haspopup="menu"
                  onClick={handleToggle}
                  disabled={isDisabled}
                  style={{
                    borderBottomRightRadius: theme.spacing(0.5),
                    borderTopRightRadius: theme.spacing(0.5),
                  }}>
                  {openCompGroupsOptions ? (
                    <ArrowDropUpIcon id="comp-groups-up" />
                  ) : (
                    <ArrowDropDownIcon id="comp-groups-down" />
                  )}
                </Button>
              </ButtonGroup>
              <CompGroupsOptions
                setOpenCompGroupsOptions={setOpenCompGroupsOptions}
                compGroupsRef={compGroupsRef}
                openCompGroupsOptions={openCompGroupsOptions}
                saveCompGroups={saveCompGroups}
                deleteCompGroup={deleteCompGroup}
                tableData={compsSheet.tableData}
                approachType={PRIVATE_TRANSACTIONS}
              />
            </Grid>
          )}
        </CustomCompactContainer>

        {/* Performance metrics */}
        <Grid className={VALUATION_GPT_PERFORMANCE_METRICS_CONTAINER_CLASS} size={{ xs: 12 }}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore: ScalarSpreadsheet is not typed */}
          <ScalarSpreadsheet
            {...performanceMetricsSheet}
            key={performanceMetricsSheet.name}
            onChange={onChange}
            sheet={performanceMetricsSheet}
            workbook={workbook}
            format={format}
            formatDispatch={formatDispatch}
            displayColumnIndicator
            displayRowIndicator
          />
        </Grid>

        <WidgetDrawer
          applicationContext={WidgetDrawerApplicationContext.TRANSACTION_COMPS}
          notesProps={{
            pageType: VALUATIONS_PAGE_VALUE,
            pageTypeKey: VALUATIONS_PAGE_KEY,
            pageTypeId: approach.id,
            notes: cachedNotes,
            isApproach: true,
            setNotes,
            onAddNote,
            onUpdateNotes,
            onDeleteNote,
            isDisabled,
          }}
        />
      </CustomContainer>

      {isExportMode && (
        <Grid
          data-ref={PdfExportTargets.TRANSACTION_COMPS_DESCRIPTIONS.id}
          size={{ xs: 12 }}
          style={EXPORT_MODE_STYLES}
          sx={{ '& .MuiTypography-root': { fontSize: '1.5em', lineHeight: 1.8 } }}>
          {currentGptDescriptionChunk.map(transaction => (
            <Grid key={`${transaction.cap_iq_transaction_id ?? transaction.row_ref}-description`} marginBottom={4}>
              <Typography variant="h3">{transaction.target_name || EMPTY_PDF_VALUE}</Typography>
              <Typography marginTop={1} variant="body2">
                {transaction.business_description || EMPTY_PDF_VALUE}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export default TransactionComps;
